import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import SearchBar from './components/SearchBar';
import BadgeListItem from './components/BadgeListItem';
import EmptyState from './components/EmptyState';
import LoadingSkeleton from './components/LoadingSkeleton';
import FilterStats from './components/FilterStats';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import BadgeService from '../../services/badgeService';

const AllBadgesPage = () => {
  const navigate = useNavigate();
  const { isSuperUser } = useAuth();
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredBadges, setFilteredBadges] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSort, setSelectedSort] = useState('recently-updated');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Load badges from Supabase
  const loadBadges = async () => {
    setLoading(true);
    try {
      const { data, error } = await BadgeService?.getBadges({
        activeOnly: selectedStatus === 'active' ? true : selectedStatus === 'inactive' ? false : undefined
      });
      
      if (error) {
        console.error('Error loading badges:', error);
        setError(error);
        setBadges([]);
        setFilteredBadges([]);
      } else {
        setBadges(data || []);
        setFilteredBadges(applySortAndFilter(data || []));
      }
    } catch (error) {
      console.error('Failed to load badges:', error);
      setError('Failed to load badges');
      setBadges([]);
      setFilteredBadges([]);
    } finally {
      setLoading(false);
    }
  };

  // Load statistics
  const loadStatistics = async () => {
    try {
      const { data, error } = await BadgeService?.getBadgeStatistics();
      if (!error && data) {
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  // Apply sorting and filtering
  const applySortAndFilter = (badgeList) => {
    let filtered = [...badgeList];

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(badge => badge?.category === selectedCategory);
    }

    // Apply status filter
    if (selectedStatus === 'active') {
      filtered = filtered?.filter(badge => badge?.is_active === true);
    } else if (selectedStatus === 'inactive') {
      filtered = filtered?.filter(badge => badge?.is_active === false);
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      switch (selectedSort) {
        case 'recently-updated':
          return new Date(b.updated_at) - new Date(a.updated_at);
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'title-asc':
          return (a?.title || '')?.localeCompare(b?.title || '');
        case 'title-desc':
          return (b?.title || '')?.localeCompare(a?.title || '');
        case 'most-assigned':
          return (b?.assignment_count || 0) - (a?.assignment_count || 0);
        case 'least-assigned':
          return (a?.assignment_count || 0) - (b?.assignment_count || 0);
        case 'points-high':
          return (b?.points || 0) - (a?.points || 0);
        case 'points-low':
          return (a?.points || 0) - (b?.points || 0);
        default:
          return 0;
      }
    });

    return filtered;
  };

  // Handle search
  const handleSearch = async (term) => {
    setSearchTerm(term);
    
    if (!term?.trim()) {
      setFilteredBadges(applySortAndFilter(badges));
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    try {
      const { data, error } = await BadgeService?.searchBadges(term?.trim(), {
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        activeOnly: selectedStatus === 'active' ? true : selectedStatus === 'inactive' ? false : undefined,
        limit: 100
      });
      
      if (error) {
        console.error('Search error:', error);
        setFilteredBadges([]);
        setError('Search failed. Please try again.');
      } else {
        setFilteredBadges(applySortAndFilter(data || []));
        setError(null);
      }
    } catch (error) {
      console.error('Search failed:', error);
      setFilteredBadges([]);
      setError('Search failed. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle filter changes
  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    if (!searchTerm) {
      setFilteredBadges(applySortAndFilter(badges));
    } else {
      handleSearch(searchTerm); // Re-search with new filter
    }
  };

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    if (!searchTerm) {
      setFilteredBadges(applySortAndFilter(badges));
    } else {
      handleSearch(searchTerm); // Re-search with new filter
    }
  };

  const handleSort = (sortType) => {
    setSelectedSort(sortType);
    setFilteredBadges(applySortAndFilter(filteredBadges));
  };

  // Handle badge click
  const handleBadgeClick = (badge) => {
    navigate(`/badge-detail-page?id=${badge?.id}`);
  };

  // Handle add new badge
  const handleAddNewBadge = () => {
    navigate('/add-badge');
  };

  // Handle badge update (refresh list)
  const handleBadgeUpdate = () => {
    loadBadges();
    loadStatistics();
  };

  // Handle refresh
  const handleRefresh = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedStatus('all');
    setSelectedSort('recently-updated');
    setError(null);
    loadBadges();
    loadStatistics();
  };

  // Load data on component mount
  useEffect(() => {
    loadBadges();
    loadStatistics();
  }, [selectedStatus]); // Reload when status filter changes

  // Update filtered badges when filters change
  useEffect(() => {
    if (!searchTerm) {
      setFilteredBadges(applySortAndFilter(badges));
    }
  }, [badges, selectedCategory, selectedSort, selectedStatus]);

  // Redirect non-superusers
  useEffect(() => {
    if (!isSuperUser()) {
      navigate('/admin-home-dashboard');
    }
  }, [isSuperUser, navigate]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!loading && badges?.length === 0 && !error) {
    return <EmptyState onAddNewBadge={handleAddNewBadge} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Badge Management - RoleCall</title>
        <meta name="description" content="Manage achievement badges and track user assignments in the RoleCall system." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        
        <main className="container mx-auto px-6 py-8 max-w-6xl">
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold text-white mb-2">
              Badge Management
            </h1>
            <p className="text-blue-100 font-body">
              Manage achievement badges and track user assignments
            </p>
          </div>

          {/* Search Bar */}
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryFilter}
            selectedStatus={selectedStatus}
            onStatusChange={handleStatusFilter}
            selectedSort={selectedSort}
            onSortChange={handleSort}
            categoryOptions={Object.keys(stats?.by_category || {})}
            totalBadges={badges?.length || 0}
          />

          {/* Filter Stats */}
          {!searchTerm && stats && (
            <FilterStats
              totalCount={badges?.length || 0}
              filteredCount={filteredBadges?.length || 0}
              searchTerm={searchTerm}
              selectedCategory={selectedCategory}
              selectedStatus={selectedStatus}
              stats={stats}
            />
          )}

          {/* Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex items-center space-x-4 flex-wrap">
              <Button
                variant="outline"
                onClick={handleRefresh}
                iconName="RefreshCw"
                iconPosition="left"
                disabled={loading}
                className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
            </div>
            
            <Button
              variant="default"
              onClick={handleAddNewBadge}
              iconName="Plus"
              iconPosition="left"
              className="bg-white text-indigo-900 hover:bg-gray-100 rounded-lg"
            >
              Add Badge
            </Button>
          </div>

          {/* Loading indicator for search */}
          {searchLoading && (
            <div className="bg-blue-50 bg-opacity-90 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="Search" size={20} className="text-blue-600 animate-pulse" />
                <p className="text-blue-800 font-medium">
                  Searching badges...
                </p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} className="text-red-800" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="mt-4 border-red-400 text-red-600 hover:bg-red-400 hover:text-white rounded-lg"
                iconName="RefreshCw"
                iconPosition="left"
              >
                Try Again
              </Button>
            </div>
          )}

          {/* No results message */}
          {filteredBadges?.length === 0 && searchTerm && !searchLoading && !error && (
            <div className="text-center py-12">
              <Icon name="Search" size={48} className="mx-auto text-blue-300 mb-4" />
              <p className="text-white text-lg mb-2">
                No badges found for "{searchTerm}"
                {selectedCategory !== 'all' && ` in ${selectedCategory} category`}
              </p>
              <p className="text-blue-200 text-sm">
                Try different keywords or adjust your filters.
              </p>
            </div>
          )}

          {/* Badges List */}
          {filteredBadges?.length > 0 && (
            <div className="space-y-4">
              {filteredBadges?.map((badge) => (
                <BadgeListItem 
                  key={badge?.id} 
                  badge={badge}
                  onUpdate={handleBadgeUpdate}
                  onClick={handleBadgeClick}
                />
              ))}
            </div>
          )}

          {/* Footer Stats */}
          {filteredBadges?.length > 0 && (
            <div className="mt-8 pt-6 border-t border-white border-opacity-20">
              <div className="flex items-center justify-between text-sm text-blue-100">
                <div className="flex items-center space-x-2">
                  <Icon name="Award" size={16} />
                  <span className="font-caption">
                    {searchTerm ? `Found ${filteredBadges?.length} of ${badges?.length}` : `Showing ${filteredBadges?.length}`} badges
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={16} />
                  <span className="font-caption">
                    Last updated: {new Date()?.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AllBadgesPage;